/**
 * ECM Progress Notes Report — org-admin view of every submitted "Progress Note"
 * across all members, in one place, with CSV export. (TRUS-265, split from
 * TRUS-240.)
 *
 * Surfaces the existing backend report GET /api/reports/ecm-progress-notes
 * (app/routers/ecm_progress_notes_report.py, org_admin/ops_admin/super_admin,
 * built under TRUS-145) which was previously not reachable from the UI. The
 * backend aggregates submitted "Progress Note" form_instances per member for
 * the caller's org, so this answers "where can I find all previous progress
 * notes from each member?" in a single consolidated, exportable view.
 *
 * Data is fetched by service-date range (server-side); the member / LCM /
 * category / status pickers filter the loaded rows client-side (their options
 * are derived from the data, so no enum is hardcoded). CSV export downloads the
 * server's CSV for the current date range — and the CSV is the complete filtered
 * set, while this table shows the first page.
 *
 * THE single implementation. The command-palette route
 * (src/app/admin/ecm-progress-notes-report/page.tsx) used to carry a second table
 * against the same endpoint with a different column set; it now wraps this one, so
 * "the progress notes report" means the same thing wherever it is opened.
 *
 * The coded columns (procedure, both modifiers, diagnosis, place of service) come
 * back reduced to the bare code — the form stores them as "<code><sep><description>",
 * which is not claimable. Do not re-render the descriptor here.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/services/apiClient';
import { Download, RefreshCw, Loader2, HeartPulse, AlertTriangle } from 'lucide-react';

interface ProgressNoteRow {
  note_id: string | number | null;
  note_status: string | null;
  submitted_at: string | null;
  created_at: string | null;
  service_date: string | null;
  note_category: string | null;
  encounter_type: string | null;
  title: string | null;
  content: string | null;
  time_spent: string | null;
  procedure_code: string | null;
  modifier_1: string | null;
  modifier_2: string | null;
  diagnosis_code: string | null;
  place_of_service: string | null;
  mcp_name: string | null;
  program_name: string | null;
  legacy_created_by: string | null;
  member_id: number | null;
  member_name: string | null;
  member_dob: string | null;
  enrollment_id: number | null;
  program_id: number | null;
  episode_id: number | null;
  ecm_status: string | null;
  episode_opened_date: string | null;
  lcm_user_id: number | null;
  lcm_name: string | null;
}

interface ReportResponse {
  total: number;
  returned: number;
  has_more: boolean;
  cbo_id: number;
  filters: Record<string, unknown>;
  data: ProgressNoteRow[];
}

const ALL = '__all__';

const fmtDate = (v: string | null): string => {
  if (!v) return '';
  // Accept both date-only and ISO timestamps; render date-only.
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toISOString().slice(0, 10);
};

const EcmProgressNotesReport: React.FC = () => {
  const [rows, setRows] = useState<ProgressNoteRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Server-side filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Client-side filters (options derived from loaded rows)
  const [member, setMember] = useState<string>(ALL);
  const [lcm, setLcm] = useState<string>(ALL);
  const [category, setCategory] = useState<string>(ALL);
  const [status, setStatus] = useState<string>(ALL);
  const [search, setSearch] = useState('');

  const serverParams = () => {
    const params: Record<string, string> = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiClient.get<ReportResponse>('/api/reports/ecm-progress-notes', {
        params: serverParams(),
      });
      setRows(resp.data?.data || []);
      setTotal(resp.data?.total ?? (resp.data?.data || []).length);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403) {
        setError('You need an organization admin role to view this report.');
      } else {
        setError(detail || e?.message || 'Failed to load progress notes.');
      }
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Initial load.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = async () => {
    setExporting(true);
    setError(null);
    try {
      const resp = await apiClient.get('/api/reports/ecm-progress-notes', {
        params: { ...serverParams(), format: 'csv' },
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ecm_progress_notes${dateFrom ? `_${dateFrom}` : ''}${dateTo ? `_${dateTo}` : ''}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to export CSV.');
    } finally {
      setExporting(false);
    }
  };

  // Derive picker options from loaded rows so we never hardcode enums.
  const memberOptions = useMemo(() => {
    const seen = new Map<string, string>();
    rows.forEach((r) => {
      if (r.member_id != null) seen.set(String(r.member_id), r.member_name || `Member ${r.member_id}`);
    });
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const lcmOptions = useMemo(() => {
    const seen = new Map<string, string>();
    rows.forEach((r) => {
      if (r.lcm_user_id != null) seen.set(String(r.lcm_user_id), r.lcm_name || `User ${r.lcm_user_id}`);
    });
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const categoryOptions = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => r.note_category && s.add(r.note_category));
    return Array.from(s).sort();
  }, [rows]);

  const statusOptions = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => r.ecm_status && s.add(r.ecm_status));
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (member !== ALL && String(r.member_id) !== member) return false;
      if (lcm !== ALL && String(r.lcm_user_id) !== lcm) return false;
      if (category !== ALL && r.note_category !== category) return false;
      if (status !== ALL && r.ecm_status !== status) return false;
      if (q) {
        const hay = `${r.member_name || ''} ${r.title || ''} ${r.content || ''} ${r.note_category || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, member, lcm, category, status, search]);

  const memberCount = useMemo(
    () => new Set(filtered.map((r) => r.member_id).filter((v) => v != null)).size,
    [filtered],
  );

  const selectCls = 'text-sm border border-gray-300 rounded-md px-2 py-1.5 max-w-[16rem]';

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <HeartPulse className="w-5 h-5 text-rose-500" />
            ECM Progress Notes
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            All submitted progress notes across members, in one place. Showing {filtered.length}
            {filtered.length !== total ? ` of ${total}` : ''} note{total === 1 ? '' : 's'}
            {memberCount ? ` across ${memberCount} member${memberCount === 1 ? '' : 's'}` : ''}.
          </p>
        </div>
        <button
          onClick={exportCsv}
          disabled={exporting || loading || rows.length === 0}
          className="inline-flex items-center gap-2 text-sm bg-gray-900 text-white rounded-md px-3 py-1.5 hover:bg-gray-800 disabled:opacity-50"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-end gap-2 flex-wrap bg-white border border-gray-200 rounded-lg p-3">
        <label className="flex flex-col text-xs text-gray-500 gap-1">
          Service date from
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
          />
        </label>
        <label className="flex flex-col text-xs text-gray-500 gap-1">
          Service date to
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
          />
        </label>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 text-sm border border-gray-300 rounded-md px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50"
          title="Apply the service-date range"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Apply
        </button>

        <div className="flex items-end gap-2 flex-wrap ml-auto">
          <select value={member} onChange={(e) => setMember(e.target.value)} className={selectCls} title="Filter by member">
            <option value={ALL}>All members</option>
            {memberOptions.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
          <select value={lcm} onChange={(e) => setLcm(e.target.value)} className={selectCls} title="Filter by assigned LCM">
            <option value={ALL}>All LCMs</option>
            {lcmOptions.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className={selectCls} title="Filter by note category">
            <option value={ALL}>All categories</option>
            {categoryOptions.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls} title="Filter by ECM episode status">
            <option value={ALL}>All statuses</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search notes…"
            className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
          />
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Service date', 'Member', 'Category', 'Title', 'Proc', 'Mod 1', 'Mod 2', 'Dx', 'POS',
                  'Time', 'Encounter', 'MCP', 'LCM', 'ECM status', 'Billing', 'Submitted'].map((h) => (
                  <th key={h} className="text-left font-medium text-gray-500 px-4 py-2 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={16} className="px-4 py-8 text-center text-gray-400">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                    Loading progress notes…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={16} className="px-4 py-8 text-center text-gray-400">
                    No progress notes match the current filters.
                  </td>
                </tr>
              ) : (
                filtered.map((r, i) => (
                  <tr key={(r.note_id as React.Key) ?? i} className="hover:bg-gray-50">
                    <td className="px-4 py-2 align-top text-gray-700 whitespace-nowrap">{fmtDate(r.service_date)}</td>
                    <td className="px-4 py-2 align-top text-gray-900 whitespace-nowrap">{r.member_name || (r.member_id != null ? `Member ${r.member_id}` : '')}</td>
                    <td className="px-4 py-2 align-top text-gray-700 whitespace-nowrap">{r.note_category || ''}</td>
                    <td className="px-4 py-2 align-top text-gray-700 max-w-[22rem]">
                      <span className="block truncate" title={`${r.title || ''}${r.content ? `\n\n${r.content}` : ''}`}>
                        {r.title || (r.content ? r.content.slice(0, 80) : '')}
                      </span>
                    </td>
                    {/* The coded fields a claim line is built from. The API reduces each
                        to the bare code; the form stores "<code><sep><description>". */}
                    <td className="px-4 py-2 align-top text-gray-900 font-mono text-xs whitespace-nowrap">{r.procedure_code || ''}</td>
                    <td className="px-4 py-2 align-top text-gray-900 font-mono text-xs whitespace-nowrap">{r.modifier_1 || ''}</td>
                    <td className="px-4 py-2 align-top text-gray-900 font-mono text-xs whitespace-nowrap">{r.modifier_2 || ''}</td>
                    <td className="px-4 py-2 align-top text-gray-900 font-mono text-xs whitespace-nowrap">{r.diagnosis_code || ''}</td>
                    <td className="px-4 py-2 align-top text-gray-900 font-mono text-xs whitespace-nowrap">{r.place_of_service || ''}</td>
                    <td className="px-4 py-2 align-top text-gray-700 whitespace-nowrap">{r.time_spent || ''}</td>
                    <td className="px-4 py-2 align-top text-gray-700 whitespace-nowrap">{r.encounter_type || ''}</td>
                    <td className="px-4 py-2 align-top text-gray-700 whitespace-nowrap">{r.mcp_name || ''}</td>
                    <td className="px-4 py-2 align-top text-gray-700 whitespace-nowrap">{r.lcm_name || ''}</td>
                    <td className="px-4 py-2 align-top text-gray-700 whitespace-nowrap">{r.ecm_status || ''}</td>
                    <td className="px-4 py-2 align-top text-gray-700 whitespace-nowrap">
                      {r.note_status && r.note_status !== 'submitted' ? r.note_status.replace('_', ' ') : ''}
                    </td>
                    <td className="px-4 py-2 align-top text-gray-500 whitespace-nowrap">{fmtDate(r.submitted_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-gray-400">
        Server returns up to 2,000 notes per query — narrow the service-date range to page through larger histories. CSV export reflects the current date range.
      </p>
    </div>
  );
};

export default EcmProgressNotesReport;
